import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { CheckboxInput } from '@calendly/ui/components/checkbox-input';
import { ProTip } from '@calendly/ui/components/pro-tip';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useCallback, forwardRef } from 'react';
import { selectById as selectCalendarAccountById, useFetchCalendarAccountsQuery, useUpdateCalendarAccountMutation } from 'features/calendar_connections';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { useTrack } from 'hooks/analytics_events/useTrack';
import { useGetUserSettingsQuery, usePatchUserSettingsMutation } from '../../../api';
const containerCss = "c1fnbo4l";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const labelCss = "l6hcvdl";
const Label = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'label';
  return <Component ref={ref} {...restProps} className={classNames(labelCss, props.className)} />;
});
const styledCheckboxInputCss = "srrgw4u";
const StyledCheckboxInput = forwardRef((props, ref) => <CheckboxInput ref={ref} {...props} className={classNames(styledCheckboxInputCss, props.className)} />);
const SyncCancellationSetting = ({
  pushAccountUuid,
  subscribable,
  webhooksEnabled,
  isLoading,
  onChangeHandler
}) => {
  const enableMeetingsServiceEventSync = useFlag('enable_meetings_service_event_sync', false);
  if (!subscribable || !pushAccountUuid) return null;
  return <Container>
      <Label>
        <StyledCheckboxInput checked={webhooksEnabled} disabled={isLoading} onChange={onChangeHandler} />
        <BodyText size="medium">
          {enableMeetingsServiceEventSync ? I18n.t('publisher.availability_settings.section.calendar_settings.adding_events.sync_changes_label') : I18n.t('publisher.availability_settings.section.calendar_settings.adding_events.sync_cancellations_label')}
        </BodyText>
      </Label>
      {enableMeetingsServiceEventSync && <ProTip hideControls spacingLeft={4}>
          {I18n.t('publisher.availability_settings.section.calendar_settings.adding_events.sync_changes_tooltip')}
        </ProTip>}
    </Container>;
};
export const SyncSettings = ({
  pushAccountUuid
}) => {
  const {
    data
  } = useFetchCalendarAccountsQuery();
  const {
    subscribable,
    webhooksEnabled
  } = pushAccountUuid ? selectCalendarAccountById(data, pushAccountUuid) : {};
  const {
    data: {
      pushBuffersToExternalCalendar,
      userUuid
    } = {
      pushBuffersToExternalCalendar: false
    }
  } = useGetUserSettingsQuery();
  const [updateUserSettings, {
    isLoading: isUserSettingsLoading
  }] = usePatchUserSettingsMutation();
  const [updateAccount, {
    isLoading
  }] = useUpdateCalendarAccountMutation();
  const track = useTrack();
  const onChangeHandler = useCallback(({
    target
  }) => {
    updateAccount({
      id: pushAccountUuid,
      webhooksEnabled: target.checked
    });
    track({
      name: TRACKING_EVENT_NAMES.SETTING_UPDATED,
      properties: {
        setting_name: 'sync_cancel_meetings_in_calendar',
        edit_type: [target.checked ? 'enable' : 'disable']
      }
    });
  }, [updateAccount, pushAccountUuid, track]);
  const onPushBuffersSettingsChange = useCallback(({
    target
  }) => {
    updateUserSettings({
      userUuid,
      user_settings: {
        push_buffers_to_external_calendar: target.checked
      }
    });
    track({
      name: TRACKING_EVENT_NAMES.SETTING_UPDATED,
      properties: {
        setting_name: 'sync_include_buffers_in_calendar',
        edit_type: [target.checked ? 'enable' : 'disable']
      }
    });
  }, [updateUserSettings, userUuid, track]);
  if (!pushAccountUuid) return null;
  return <>
      <BodyText weight="bold">
        {I18n.t('publisher.availability_settings.section.calendar_settings.adding_events.sync_settings')}
      </BodyText>
      <Container>
        <Label>
          <StyledCheckboxInput checked={pushBuffersToExternalCalendar} disabled={isUserSettingsLoading} onChange={onPushBuffersSettingsChange} />
          <BodyText size="medium">
            {I18n.t('publisher.availability_settings.section.calendar_settings.adding_events.include_buffers_on_calendar_label')}
          </BodyText>
        </Label>
        <ProTip hideControls spacingLeft={4}>
          {I18n.t('publisher.availability_settings.section.calendar_settings.adding_events.include_buffers_on_calendar_tooltip')}
        </ProTip>
      </Container>
      <SyncCancellationSetting pushAccountUuid={pushAccountUuid} subscribable={subscribable} webhooksEnabled={webhooksEnabled} onChangeHandler={onChangeHandler} isLoading={isLoading} />
    </>;
};

require("./sync_settings.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./sync_settings.js");